// Befehls-Register des Editors: die eine Wahrheit für Befehlspalette (Strg/Cmd+K),
// Tastenkürzel und die Kürzel in den Tooltips der Werkzeugleiste.
//
// Ein Eintrag beschreibt genau ein Werkzeug:
//   id       stabiler Schlüssel (nur hier, nicht im DOM) — eindeutig
//   label    Beschriftung in der Palette (deutsch, wie im Werkzeug selbst)
//   hint     optionaler Halbsatz, erscheint als Tooltip der Palettenzeile
//   group    Überschrift in der Kürzel-Übersicht (leere Eingabe)
//   shortcut optionales Kürzel, "p" / "Shift+H" / "Mod+Z" ("Mod" = Strg bzw. Cmd)
//   method   Name der Methode auf dem editor_controller — ein Vitest prüft,
//            dass es sie wirklich gibt (sonst bricht ein Umbenennen die Palette)
//   params   optionale Stimulus-Parameter, die Methoden wie setView erwarten
//   modes    Modi, in denen der Befehl laufen darf — ohne Angabe nur "normal"
//
// Kürzel-Vergabe folgt CAD-Konvention (P/B/K/S/M/G/D/E/X/F, 1–6 für die
// Standard-Ansichten). Sie feuern nur außerhalb von Eingabefeldern und
// solange kein Dialog offen ist — das prüft der Controller.

// --- Modi -------------------------------------------------------------------
// Der Editor kennt drei exklusive Modi, die die normale Werkzeugleiste
// verdrängen bzw. die Szene zur reinen Präsentation machen: Skizze, Foto und
// Montage. Sonst gilt "normal".
//
// Regel für die Kennzeichnung (bewusst restriktiv, Standard = nur "normal"):
// Ein Befehl bleibt im Modus nur dann erlaubt, wenn er
//   a) nichts am Dokument oder an der Auswahl ändert, was der Modus verbietet,
//      und keine Bedienelemente braucht, die der Modus ausblendet, oder
//   b) zum Modus selbst gehört (sein Ausstieg).
// Daraus folgen genau drei Töpfe:
//   * modusneutral (ANY_MODE): Rückgängig/Wiederholen — ein Fehlgriff muss
//     überall zurücknehmbar sein; die sechs Standard-Ansichten 1–6 — reine
//     Kamerabefehle, im Foto-Modus sogar der Hauptzweck; die Befehlspalette
//     selbst — sonst käme man an die Übersicht nicht heran und sähe nie,
//     was gerade gesperrt ist.
//   * modus-eigen: "Foto-Ansicht" (F) schaltet den Foto-Modus auch wieder aus,
//     und der 3D-Export ist reine Ausgabe — er passt zur Präsentationsansicht.
//   * alles andere nur "normal". Insbesondere die rechten Panels (Teile-Liste,
//     Vorlagen, Variablen, Prüfung, Details): Skizze, Foto und Montage
//     schließen sie beim Betreten, sie gehören zum Normalmodus.
export const MODES = [ "normal", "sketch", "photo", "assembly" ]

export const MODE_LABELS = {
  normal: "Normalmodus",
  sketch: "Skizzenmodus",
  photo: "Foto-Modus",
  assembly: "Montage-Modus"
}

const DEFAULT_MODES = [ "normal" ]
const ANY_MODE = MODES
const PHOTO_TOO = [ "normal", "photo" ]

const COMMANDS = [
  // --- Erzeugen -------------------------------------------------------------
  { id: "add-plate", label: "Platte", hint: "Platte im Standardmaß einfügen",
    group: "Erzeugen", shortcut: "p", method: "addPlate" },
  { id: "add-beam", label: "Balken", hint: "Balken im Standardmaß einfügen",
    group: "Erzeugen", shortcut: "b", method: "addBeam" },
  { id: "add-cabinet", label: "Korpus…", hint: "Korpus aus Maßen erzeugen",
    group: "Erzeugen", shortcut: "k", method: "openCabinetDialog" },
  { id: "add-drawer", label: "Schublade / Tür…", hint: "Schublade oder Tür/Front erzeugen",
    group: "Erzeugen", shortcut: "t", method: "openDrawerDialog" },
  { id: "start-sketch", label: "Skizze", hint: "Skizze zeichnen und extrudieren",
    group: "Erzeugen", shortcut: "s", method: "startSketch" },
  { id: "insert-template", label: "Vorlagen", hint: "Vorlagen-Panel öffnen",
    group: "Erzeugen", shortcut: "v", method: "toggleTemplatesPanel" },

  // --- Werkzeuge ------------------------------------------------------------
  { id: "measure", label: "Messen", hint: "Abstand zwischen zwei Punkten messen",
    group: "Werkzeuge", shortcut: "m", method: "toggleMeasure" },
  { id: "multi-select", label: "Mehrfachauswahl", hint: "Mehrere Teile gemeinsam bearbeiten",
    group: "Werkzeuge", shortcut: "e", method: "toggleMultiSelect" },
  { id: "section", label: "Schnittansicht", hint: "Schnittebene durch die Szene legen",
    group: "Werkzeuge", shortcut: "x", method: "toggleSection" },
  // F ist zugleich der Ausstieg aus dem Foto-Modus — dort also erlaubt
  { id: "photo", label: "Foto-Ansicht", hint: "Aufgeräumte Ansicht zum Abfotografieren",
    group: "Werkzeuge", shortcut: "f", method: "togglePhoto", modes: PHOTO_TOO },
  { id: "assembly", label: "Montageanleitung", hint: "Aufbau Schritt für Schritt zeigen",
    group: "Werkzeuge", shortcut: "a", method: "startAssembly" },
  { id: "room", label: "Raummaß…", hint: "Nische oder Raum als Begrenzung festlegen",
    group: "Werkzeuge", method: "openRoomDialog" },

  // --- Bearbeiten (Auswahl) -------------------------------------------------
  { id: "duplicate", label: "Duplizieren", hint: "Auswahl kopieren und versetzen",
    group: "Bearbeiten", shortcut: "d", method: "duplicate" },
  { id: "mirror", label: "Spiegeln", hint: "Auswahl an der Mitte spiegeln",
    group: "Bearbeiten", method: "mirror" },
  { id: "group", label: "Gruppieren", hint: "Gewählte Teile zu einer Baugruppe",
    group: "Bearbeiten", shortcut: "g", method: "groupSelection" },
  { id: "ungroup", label: "Gruppe aufheben", group: "Bearbeiten",
    shortcut: "Shift+G", method: "ungroup" },
  { id: "relation", label: "Beziehung herstellen", hint: "Ausrichten oder ans Referenzteil heften",
    group: "Bearbeiten", shortcut: "r", method: "startAlignPick" },
  { id: "pattern", label: "Muster…", hint: "Kopien entlang einer Achse",
    group: "Bearbeiten", method: "openPatternDialog" },
  { id: "hole-row", label: "Lochreihe…", hint: "Reihenbohrungen im 32-mm-Raster",
    group: "Bearbeiten", shortcut: "l", method: "openHoleRowDialog" },
  { id: "save-template", label: "Als Vorlage speichern…", group: "Bearbeiten",
    method: "openTemplateDialog" },
  { id: "remove", label: "Löschen", hint: "Auswahl aus der Szene entfernen",
    group: "Bearbeiten", shortcut: "Delete", method: "removeSelected" },

  // --- Verlauf --------------------------------------------------------------
  // modusneutral: ein Fehlgriff muss überall zurücknehmbar bleiben
  { id: "undo", label: "Rückgängig", group: "Verlauf", shortcut: "Mod+Z", method: "undo",
    modes: ANY_MODE },
  { id: "redo", label: "Wiederholen", group: "Verlauf", shortcut: "Mod+Shift+Z", method: "redo",
    modes: ANY_MODE },

  // --- Ansicht --------------------------------------------------------------
  // Die Standard-Ansichten bewegen nur die Kamera — in jedem Modus erlaubt
  { id: "view-iso", label: "Ansicht ISO", group: "Ansicht", shortcut: "1",
    method: "setView", params: { view: "iso" }, modes: ANY_MODE },
  { id: "view-top", label: "Ansicht Oben", group: "Ansicht", shortcut: "2",
    method: "setView", params: { view: "top" }, modes: ANY_MODE },
  { id: "view-front", label: "Ansicht Vorn", group: "Ansicht", shortcut: "3",
    method: "setView", params: { view: "front" }, modes: ANY_MODE },
  { id: "view-back", label: "Ansicht Hinten", group: "Ansicht", shortcut: "4",
    method: "setView", params: { view: "back" }, modes: ANY_MODE },
  { id: "view-left", label: "Ansicht Links", group: "Ansicht", shortcut: "5",
    method: "setView", params: { view: "left" }, modes: ANY_MODE },
  { id: "view-right", label: "Ansicht Rechts", group: "Ansicht", shortcut: "6",
    method: "setView", params: { view: "right" }, modes: ANY_MODE },
  { id: "toggle-hidden", label: "Teil aus-/einblenden", group: "Ansicht",
    shortcut: "h", method: "toggleHiddenSelection" },
  { id: "isolate", label: "Isolieren", hint: "Alle anderen Teile ausblenden",
    group: "Ansicht", shortcut: "i", method: "isolateSelection" },
  { id: "show-all", label: "Alle Teile einblenden", group: "Ansicht",
    shortcut: "Shift+H", method: "showAllParts" },
  { id: "parts-list", label: "Teile-Liste", group: "Ansicht", method: "togglePartsList" },
  { id: "details", label: "Details des Teils", group: "Ansicht", method: "togglePanelInfo" },
  // reine Ausgabe, ändert nichts — passt zur Präsentationsansicht
  { id: "export-gltf", label: "3D-Export (.glb)", hint: "Sichtbare Teile als glTF sichern",
    group: "Ansicht", method: "exportGltf", modes: PHOTO_TOO },
  // modusneutral: sonst käme man im Modus nicht mehr an die Übersicht heran
  { id: "command-palette", label: "Befehle suchen", hint: "Diese Palette öffnen und schließen",
    group: "Ansicht", shortcut: "Mod+K", method: "togglePalette", modes: ANY_MODE }
]

export default COMMANDS

// --- Modi-Abfragen ----------------------------------------------------------

// Modi eines Befehls; ohne eigene Kennzeichnung gilt „nur im Normalmodus“.
export function modesFor(command) {
  return command?.modes ?? DEFAULT_MODES
}

export function isAllowedInMode(command, mode = "normal") {
  return modesFor(command).includes(mode)
}

// Deutscher Name des Modus für Statusmeldung und Palette.
export function modeLabel(mode) {
  return MODE_LABELS[mode] ?? MODE_LABELS.normal
}

// --- Suche ------------------------------------------------------------------

// Vergleichsform: kleingeschrieben und ohne Diakritika, damit „tur" die „Tür"
// findet (Umlaute zerlegen in Buchstabe + Zeichen, das Zeichen fällt weg).
// ß wird zu ss, damit „grosse" auch „Große" trifft.
function normalize(text) {
  return String(text ?? "")
    .toLowerCase()
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
}

// Je kleiner, desto weiter oben: Label-Präfix schlägt Wortanfang schlägt
// Teilstring, Gruppentreffer kommen dahinter. Infinity = kein Treffer.
function scoreFor(command, token) {
  const label = normalize(command.label)
  const group = normalize(command.group)
  if (label.startsWith(token)) return 0
  if (label.split(/[^\p{L}\p{N}]+/u).some((word) => word.startsWith(token))) return 1
  if (label.includes(token)) return 2
  if (group.startsWith(token)) return 3
  if (group.includes(token)) return 4
  return Infinity
}

// Unscharfe Suche über Label und Gruppe. Mehrere Wörter müssen alle
// vorkommen (Reihenfolge egal), gewertet wird nach dem ersten Wort.
// Leere Eingabe = ganzes Register in Registerreihenfolge (Kürzel-Übersicht).
export function searchCommands(registry, query) {
  const tokens = normalize(query).split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return [ ...registry ]
  return registry
    .map((command) => ({ command, score: scoreFor(command, tokens[0]) }))
    .filter(({ command, score }) => score < Infinity && tokens.every((t) => scoreFor(command, t) < Infinity))
    .sort((a, b) => a.score - b.score) // stabil: gleiche Wertung behält Registerreihenfolge
    .map(({ command }) => command)
}

// --- Kürzel -----------------------------------------------------------------

const MODIFIERS = { mod: "mod", shift: "shift", alt: "alt" }

function parseShortcut(shortcut) {
  const parts = String(shortcut).split("+")
  const key = parts[parts.length - 1]
  const mods = parts.slice(0, -1).map((part) => MODIFIERS[part.toLowerCase()])
  return {
    key: normalizeKey(key),
    mod: mods.includes("mod"),
    shift: mods.includes("shift"),
    alt: mods.includes("alt")
  }
}

// Buchstaben/Ziffern case-egal, benannte Tasten (Delete, Escape) bleiben stehen
function normalizeKey(key) {
  return String(key ?? "").length === 1 ? String(key).toLowerCase() : String(key ?? "")
}

export function matchesShortcut(shortcut, event) {
  if (!shortcut || !event?.key) return false
  const wanted = parseShortcut(shortcut)
  if (Boolean(event.metaKey || event.ctrlKey) !== wanted.mod) return false
  if (Boolean(event.shiftKey) !== wanted.shift) return false
  if (Boolean(event.altKey) !== wanted.alt) return false
  return normalizeKey(event.key) === wanted.key
}

// Tastendruck → Befehl. Nicht registrierte Modifier-Kombinationen (Strg+P,
// Alt+M …) treffen bewusst nichts, damit Browser-Kürzel unangetastet bleiben.
export function commandForShortcut(registry, event) {
  return registry.find((command) => matchesShortcut(command.shortcut, event)) ?? null
}

export function shortcutFor(registry, id) {
  return registry.find((command) => command.id === id)?.shortcut ?? null
}

const KEY_LABELS = { Delete: "Entf", Backspace: "Rück", Escape: "Esc", Enter: "Enter" }

// Menschenlesbares Kürzel für Palette und Tooltips („Strg+Umschalt+Z").
export function shortcutLabel(shortcut, mac = false) {
  if (!shortcut) return ""
  const parts = String(shortcut).split("+")
  const key = parts[parts.length - 1]
  return [
    ...parts.slice(0, -1).map((part) => {
      const name = part.toLowerCase()
      if (name === "mod") return mac ? "⌘" : "Strg"
      if (name === "shift") return "Umschalt"
      return "Alt"
    }),
    KEY_LABELS[key] ?? (key.length === 1 ? key.toUpperCase() : key)
  ].join("+")
}

// --- Übersicht --------------------------------------------------------------

// Befehle nach Gruppen gebündelt, Reihenfolge wie im Register — Grundlage
// der Kürzel-Übersicht in der Palette (leere Eingabe).
export function commandsByGroup(registry) {
  const groups = []
  for (const command of registry) {
    const last = groups[groups.length - 1]
    if (last && last.group === command.group) last.commands.push(command)
    else groups.push({ group: command.group, commands: [ command ] })
  }
  return groups
}
